import React from 'react';
import { CardActionsMenu } from '@/shared/components/ui';
import { PrivacyText } from '@/shared/components/ui/PrivacyText';
import { Icon } from '@/shared/icons';
import { formatPhoneNumber } from '@/shared/utils';
import { formatQueueWait } from '../../utils/time';
import { COOLDOWN_COLORS, resolveConversationCooldown } from '../../utils/cooldown';
import {
  AssigneeAvatar,
  CardSeparator,
  ChannelMeta,
  ContactAvatar,
  ConversationCardShell,
  CooldownTrack,
  RelativeTime,
} from './parts';
import type { ConversationCardFacts, ConversationCardViewProps } from './types';

/** Rótulo de bloco — `.ac4-caption` do mockup. */
const CAPTION: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  lineHeight: 1,
  color: 'var(--theme-muted-text)',
};

/**
 * Fundo do bloco de atribuição. É ele que "vira o alerta quando algo está fora
 * do lugar" — a tese do card. Ordem importa: conversa aberta sem responsável é
 * pior que IA pausada com responsável.
 */
const assignmentBackground = (facts: ConversationCardFacts): string => {
  if (!facts.assigneeName && facts.status !== 'closed') return 'var(--theme-warning-subtle)';
  if (facts.agentChip?.label === 'IA pausada') return 'var(--theme-primary-background)';
  return 'transparent';
};

/**
 * Atendimento 20 — "Seções com divisor" · papel DETALHES.
 *
 * Mockup aprovado (versão corrigida): comparacao.html:1503–1548 · CSS :568–596.
 */
export const ConversationCardDetailed: React.FC<ConversationCardViewProps> = ({
  facts,
  actions,
  onCardClick,
}) => {
  // Tempo em fila ANTES do atendimento. `undefined` quando não há `createdAt`
  // (shape completo, fora do board) — aí a linha simplesmente não aparece.
  const queueWait = formatQueueWait(facts.createdAt, facts.startedAt);
  // Barra de esfriamento em TODAS as densidades (decisão do dono, 16/08): o
  // tempo sem cor não se identifica — o mecanismo do card 06 vale aqui também.
  const cooldown = resolveConversationCooldown(facts.lastMessageAt, {
    waitingOnUs: facts.waitingOnUs,
    ...(facts.sla ? { sla: facts.sla } : {}),
  });
  const cooldownColors = COOLDOWN_COLORS[cooldown.level];

  return (
    <ConversationCardShell conversationId={facts.id} density="detailed" gap={8} onClick={onCardClick}>
      {/* Kebab no canto: a linha do rótulo "CONTATO" está livre. O gatilho tem
          24px (size sm) e o rótulo mede 11px a 14px do topo — 8 + 24 = 32 termina
          antes de a linha do nome começar, em 33. Nada se sobrepõe. */}
      {actions.length > 0 && (
        <div className="absolute" style={{ top: 8, right: 8, zIndex: 1 }}>
          <CardActionsMenu size="sm" actions={actions} data-testid={`attendance-card-menu-${facts.id}`} />
        </div>
      )}

      {/* 1 · QUEM É */}
      <div className="min-w-0">
        <span style={CAPTION}>Contato</span>
        <div className="flex items-center gap-2 min-w-0" style={{ marginTop: 8 }}>
          <ContactAvatar name={facts.contactName} picture={facts.contactPicture} size={28} />
          <div className="flex-1 min-w-0">
            <p
              className="truncate"
              style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.35, color: 'var(--theme-text)' }}
            >
              {facts.contactName}
            </p>
            {/* Ausência de telefone é o NORMAL de Instagram, Telegram e widget:
                mesmo tamanho, mesma cor, sem alerta. Ver decisão 2 desta tarefa. */}
            <span
              className="flex items-center gap-1.5 min-w-0"
              style={{ marginTop: 2, fontSize: 12, color: 'var(--theme-muted-text)' }}
            >
              <Icon name="phone" size={14} />
              <PrivacyText className="truncate min-w-0">
                {facts.contactPhone ? formatPhoneNumber(facts.contactPhone) : 'Sem telefone'}
              </PrivacyText>
            </span>
          </div>
        </div>
      </div>

      <CardSeparator />

      {/* 2 · O QUE ACONTECEU */}
      <div className="min-w-0">
        <span style={CAPTION}>Conversa</span>
        <p
          className="min-w-0"
          data-testid={`attendance-card-preview-${facts.id}`}
          style={{
            marginTop: 6,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontSize: 12,
            lineHeight: 1.45,
            color: 'var(--theme-muted-text)',
          }}
        >
          {facts.lastMessage || 'Sem mensagens'}
        </p>
        <div className="flex items-center justify-between gap-2 min-w-0" style={{ marginTop: 8 }}>
          <ChannelMeta providerId={facts.providerId} label={facts.channelLabel} />
          <RelativeTime iso={facts.lastMessageAt} style="long" color={cooldownColors.fg} />
        </div>
        <div
          className="flex items-center min-w-0"
          style={{ marginTop: 8 }}
          data-testid={`attendance-card-cooldown-${facts.id}`}
          data-cooldown-level={cooldown.level}
          data-cooldown-source={cooldown.source}
        >
          <CooldownTrack ratio={cooldown.ratio} color={cooldownColors.solid} />
        </div>
      </div>

      {/* 3 · QUEM RESPONDE — sangrado até as bordas do card. O raio inferior vem
          do `overflow: hidden` da casca. */}
      <div
        className="min-w-0"
        style={{
          margin: '4px -14px -14px',
          padding: '12px 14px 14px',
          borderTop: '1px solid var(--theme-card-border)',
          backgroundColor: assignmentBackground(facts),
        }}
      >
        <span style={CAPTION}>Atribuição</span>
        <div className="flex items-center justify-between gap-2 min-w-0" style={{ marginTop: 8 }}>
          <span className="flex items-center gap-2 min-w-0">
            <AssigneeAvatar name={facts.assigneeName} picture={facts.assigneePicture} size={24} />
            <span
              className="truncate min-w-0"
              style={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--theme-text)' }}
            >
              {facts.assigneeName ?? 'Sem responsável'}
            </span>
          </span>
          {facts.agentChip && (
            // O mockup usa borda roxa com alpha cravado (rgba(139,92,246,.35)).
            // No app o mesmo significado já tem par de tokens branco-marca:
            // --theme-ai-badge-background / --theme-ai-badge-text.
            <span
              className="inline-flex items-center gap-1 flex-none"
              style={{
                padding: '1px 8px',
                borderRadius: 9999,
                fontSize: 11,
                fontWeight: 500,
                lineHeight: '18px',
                backgroundColor: 'var(--theme-ai-badge-background)',
                color: 'var(--theme-ai-badge-text)',
              }}
            >
              <Icon name={facts.agentChip.icon} size={12} />
              {facts.agentChip.label}
            </span>
          )}
        </div>
        {queueWait && (
          <p
            data-testid={`attendance-card-queue-${facts.id}`}
            style={{ marginTop: 6, fontSize: 11, lineHeight: 1.2, color: 'var(--theme-muted-text)' }}
          >
            {queueWait}
          </p>
        )}
      </div>
    </ConversationCardShell>
  );
};
